import axios from "axios";
import { createGetCache } from "@/lib/apiCache";
import { resolveApiBaseUrl } from "@/lib/resolveApiBaseUrl";

export const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").trim().replace(/\/$/, "");

export const API = axios.create({
  baseURL: resolveApiBaseUrl(),
  withCredentials: true,
});

const getCache = createGetCache({ ttlMs: 15000 });
const originalGet = API.get.bind(API);
API.get = (url, config) => getCache.wrapGet(originalGet, url, config);

export const invalidateApiCache = (prefix) => getCache.invalidate(prefix);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve();
  });
  failedQueue = [];
};

let onSessionExpired = null;

/** Register handler invoked when refresh fails (e.g. clear auth state). */
export const setSessionExpiredHandler = (handler) => {
  onSessionExpired = handler;
};

const AUTH_SKIP_REFRESH = [
  "/auth/login",
  "/auth/register",
  "/auth/forgot-password",
  "/auth/reset-password",
  "/auth/refresh",
  "/auth/logout",
];

const shouldSkipRefresh = (url = "") =>
  AUTH_SKIP_REFRESH.some((path) => url.includes(path));

API.interceptors.response.use(
  (response) => {
    const method = (response.config?.method || "").toLowerCase();
    if (method && method !== "get") {
      getCache.invalidate();
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (
      !originalRequest ||
      error.response?.status !== 401 ||
      originalRequest._retry ||
      shouldSkipRefresh(originalRequest.url)
    ) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(() => API(originalRequest));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      await API.post("/auth/refresh");
      processQueue(null);
      return API(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError);
      getCache.invalidate();
      if (onSessionExpired) onSessionExpired();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export const formatError = (error) => {
  const detail = error.response?.data?.detail;
  if (!detail) return error.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  }
  return String(detail);
};

export const uploadThumbnail = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await API.post("/uploads/thumbnail", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

export const uploadCertificateBackground = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await API.post("/uploads/certificate-background", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

/** Best-effort cleanup of an uploaded background; the backend keeps it if a template still uses it. */
export const deleteCertificateBackground = async (url) => {
  const { data } = await API.delete("/uploads/certificate-background", { params: { url } });
  return data;
};

/** Download a certificate PDF and trigger a browser save, used for both issued certificates and previews. */
export const downloadPdfBlob = async (url, filename, payload) => {
  let response;
  try {
    response = payload
      ? await API.post(url, payload, { responseType: "blob" })
      : await API.get(url, { responseType: "blob" });
  } catch (error) {
    if (error.response?.data instanceof Blob) {
      try { error.response.data = JSON.parse(await error.response.data.text()); } catch { /* use default error */ }
    }
    throw error;
  }
  const blobUrl = window.URL.createObjectURL(
    new Blob([response.data], { type: "application/pdf" })
  );
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
};

export const uploadMaterial = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await API.post("/uploads/material", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

export const requestPasswordReset = async (email, website = "", nextPath = "/dashboard") => {
  const { data } = await API.post("/auth/forgot-password", { email, website, next_path: nextPath });
  return data;
};

export const resetPassword = async (token, newPassword) => {
  const { data } = await API.post("/auth/reset-password", {
    token,
    new_password: newPassword,
  });
  return data;
};
